import { Outlet, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { AuthorizationErrorCode, ManagerAuthorization } from '@common/enums/index';
import { AutoSwitchOrganization } from '@frontend/components/elements/AutoSwitchOrganization';
import { getIsKsyosOrganization } from '@frontend/services/api/organizationsApiService';
import { assertHasAuthorization } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IOrganizationWaitingTimesAccess } from './utils/waitingTimesUtils';
import { PageTemplate } from '../../templates/PageTemplate';
import { KsyosOrganization } from '../ksyosOrganization/KsyosOrganization';
import { OrganizationNavigation } from './OrganizationNavigation';
import { OrganizationProfile } from './OrganizationProfile';
import { fetchWaitingTimesAccess } from './utils/waitingTimesUtils';

export enum EditForm {
    generalSettings = 'generalSettings',
}

export function OrganizationView() {
    const abortSignal = useAbortSignal();
    const { organizationId } = useParams() as { organizationId: string };
    const { isManager, organizationId: ownOrganizationId } = useSelector((state: IReduxState) => state.authentication);

    const [isKsyos, setIsKsyos] = useState<boolean>();

    const [organizationWaitingTimesAccess, setOrganizationWaitingTimesAccess] =
        useState<IOrganizationWaitingTimesAccess>({
            canEditWaitingTimes: false,
            canReadWaitingTimes: false,
            hasWaitingTimes: false,
        });

    useEffect(() => {
        fetchIsKsyos(organizationId);
    }, [organizationId]);

    useEffect(() => {
        if (isKsyos === false) {
            getOrganizationWaitingTimeAccess();
        }
    }, [isKsyos]);

    if (!organizationId) {
        return null;
    }

    return (
        <AutoSwitchOrganization
            forwardTo={(updatedSession) => `/organizations/${updatedSession.organizationId}/view/organizationPayouts`}
            showWarningModal={true}
        >
            {isKsyos && <KsyosOrganization organizationId={organizationId} />}
            {isKsyos === false && canSeeOrganizationPage() && (
                <PageTemplate
                    sideBarNavigation={
                        <OrganizationNavigation
                            organizationId={organizationId}
                            organizationWaitingTimesAccess={organizationWaitingTimesAccess}
                        />
                    }
                    sideBarProfile={
                        <OrganizationProfile
                            organizationId={organizationId}
                            size="large"
                        />
                    }
                    topBarNavigation={
                        <OrganizationNavigation
                            organizationId={organizationId}
                            organizationWaitingTimesAccess={organizationWaitingTimesAccess}
                        />
                    }
                    topBarProfile={
                        <OrganizationProfile
                            organizationId={organizationId}
                            size="small"
                        />
                    }
                    width="small"
                >
                    <Outlet />
                </PageTemplate>
            )}
        </AutoSwitchOrganization>
    );

    async function getOrganizationWaitingTimeAccess() {
        const result = await fetchWaitingTimesAccess(organizationId);
        setOrganizationWaitingTimesAccess(result);
    }

    function canSeeOrganizationPage() {
        if (isManager) {
            assertHasAuthorization(ManagerAuthorization.organizationRead);
        } else if (ownOrganizationId !== organizationId) {
            return new Error(AuthorizationErrorCode.CANNOT_VIEW_OTHER_ORGANIZATIONS_PROFILE);
        }

        return true;
    }

    async function fetchIsKsyos(orgId: string) {
        const isKsyosOrganization = await getIsKsyosOrganization(orgId, abortSignal);

        setIsKsyos(isKsyosOrganization);
    }
}
